import {
  WSElement,
  WSPage,
  WSPageToolbar
} from "@wingspanhq/fe-component-library";
import React from "react";
import { Redirect } from "react-router-dom";
import { Tabs } from "../../../../components/Tabs";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { routeHelpersMyResourcesTabs } from "../../../MyResources/paths";
import { BenefitCard } from "../../components/BenefitCard";
import { Benefit } from "../../data";
import {
  BENEFITS_SECTIONS_BY_SLUG,
  BenefitsSection,
  BenefitsTab
} from "../../data/sections";
import { useBenefitsCatalog } from "../../hooks/useBenefitsCatalog";
import { useBenefitsEnrollments } from "../../hooks/useBenefitsEnrollments";
import { useRequestBenefitTypeform } from "../../hooks/useRequestBenefitTypeform";
import {
  PATH_BENEFITS_PERKS,
  routeHelpersBenefitDetail,
  routeHelpersBenefitsSection,
  routeHelpersBenefitsSectionTab
} from "../../paths";

export const RouteBenefitsList: React.FC = () => {
  // Mounted on both `/:section` and `/:section/:tab`. Reading the section from
  // the two-parameter pattern leaves it undefined on the section-only URL the
  // sidebar links to, which redirects the page to itself and renders nothing.
  const { params: sectionParams } = routeHelpersBenefitsSection.useData();
  const { params: tabParams } = routeHelpersBenefitsSectionTab.useData();
  const section: BenefitsSection | undefined = (
    BENEFITS_SECTIONS_BY_SLUG as Record<string, BenefitsSection | undefined>
  )[sectionParams.section];
  const tab = section?.tabs.find(
    (sectionTab: BenefitsTab) => sectionTab.slug === tabParams.tab
  );

  const catalog = useBenefitsCatalog({ section, tab });
  const enrollments = useBenefitsEnrollments({ enabled: !!section });
  const openRequestBenefit = useRequestBenefitTypeform();
  const goToDetail = routeHelpersBenefitDetail.useGoTo();
  const goToMyResources = routeHelpersMyResourcesTabs.useGoTo();

  useBrowserPageTitle(section ? `Benefits - ${section.title}` : "Benefits");

  if (!section) {
    return <Redirect to={PATH_BENEFITS_PERKS} />;
  }

  if (!tab) {
    return (
      <Redirect
        to={routeHelpersBenefitsSectionTab.createLocationObject({
          section: section.slug,
          tab: section.tabs[0].slug
        })}
      />
    );
  }

  const listPath = routeHelpersBenefitsSectionTab.createLocationObject({
    section: section.slug,
    tab: tab.slug
  }).pathname as string;

  // An enrolled member is sent to their policy rather than back to the
  // marketing page for a benefit they already have.
  const openBenefit = (benefit: Benefit) => {
    if (enrollments.isEnrolled(benefit)) {
      goToMyResources({
        tab: benefit.isAssurely
          ? "independent-contractor-insurance"
          : "personal-insurance"
      });
    } else {
      goToDetail({ slug: benefit.slug }, { state: { backPath: listPath } });
    }
  };

  return (
    <WSPage>
      <WSPageToolbar
        title={section.title}
        actions={
          section.canRequestBenefit
            ? [
                {
                  label: "Request a new benefit",
                  buttonKind: "Primary",
                  onClick: openRequestBenefit
                }
              ]
            : undefined
        }
      >
        {section.intro}
      </WSPageToolbar>

      {section.tabs.length > 1 && (
        <Tabs
          tabs={section.tabs.map((sectionTab: BenefitsTab) => ({
            label: sectionTab.label,
            path: routeHelpersBenefitsSectionTab.createLocationObject({
              section: section.slug,
              tab: sectionTab.slug
            }).pathname as string
          }))}
        />
      )}

      {tab.description}

      <WSElement>
        {catalog.benefits.map(benefit => (
          <BenefitCard
            key={benefit.slug}
            benefit={benefit}
            isEnrolled={enrollments.isEnrolled(benefit)}
            isLoading={enrollments.isLoading}
            onClick={() => openBenefit(benefit)}
          />
        ))}
      </WSElement>
    </WSPage>
  );
};
